import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CheckCircle, Loader2, History, UserPlus } from 'lucide-react';
import { clientsAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate } from '@shared/utils';
import ClientDetailsModal from '../components/ClientDetailsModal';
import AssignClientModal from '../components/AssignClientModal';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const currentDate = new Date();
const currentMonth = currentDate.getMonth() + 1;
const currentYear = currentDate.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

export default function ServiceHistoryOverview() {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [clientToAssign, setClientToAssign] = useState(null);

  const { data, isLoading, error } = useQuery(
    ['serviceHistoryOverview', month, year],
    () => clientsAPI.getServiceHistoryOverview({ month, year }),
    { keepPreviousData: true }
  );

  const clients = data?.data?.clients ?? [];
  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => (a.serviced === b.serviced ? 0 : a.serviced ? 1 : -1)),
    [clients]
  );
  const servicedCount = clients.filter((row) => row.serviced).length;
  const incompleteCount = clients.length - servicedCount;

  const handleClientClick = (row) => {
    setSelectedClient(row.client);
    setIsDetailsModalOpen(true);
  };

  const handleAssignClose = () => {
    queryClient.invalidateQueries(['serviceHistoryOverview', month, year]);
    setClientToAssign(null);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="h-7 w-7 text-primary-600" />
          Service History Overview
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          View regular service clients by month and see which were serviced (completed call in the service month or the month before/after).
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="service-history-month" className="text-sm font-medium text-gray-700">
            Month
          </label>
          <select
            id="service-history-month"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={i} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="service-history-year" className="text-sm font-medium text-gray-700">
            Year
          </label>
          <select
            id="service-history-year"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        {!isLoading && clients.length > 0 && (
          <p className="text-sm text-gray-600">
            {servicedCount} of {clients.length} clients serviced
            {incompleteCount > 0 && `, ${incompleteCount} not complete`}
          </p>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="px-6 py-8 text-center text-red-600">
            {error.response?.data?.message || error.message || 'Failed to load service history'}
          </div>
        ) : clients.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No contract clients are due for service in {MONTH_NAMES[month - 1]} {year}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serviced
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedClients.map((row) => {
                  const c = row.client;
                  const displayName = c.isCommercial && c.companyName
                    ? c.companyName
                    : `${c.contactFirstName || ''} ${c.contactLastName || ''}`.trim();
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleClientClick(row)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {c.flags && c.flags.length > 0 && (
                            <div className="flex items-center gap-1">
                              {c.flags.slice(0, 4).map((flag) => (
                                <FlagIcon
                                  key={flag.id}
                                  iconName={flag.icon}
                                  className={`h-4 w-4 ${flag.isActive ? 'text-gray-600' : 'text-gray-400'}`}
                                  title={flag.name}
                                />
                              ))}
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900">
                            {displayName || '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {row.serviced ? (
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                            <span className="text-sm">
                              {formatDate(row.completedDate)}
                            </span>
                          </div>
                        ) : row.resolution === 'skipped' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Skipped
                          </span>
                        ) : row.resolution === 'bumped' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Bumped
                          </span>
                        ) : row.assignedTo ? (
                          <span className="text-sm text-amber-700 font-medium">
                            Assigned to {row.assignedTo.firstName} {row.assignedTo.lastName}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        {!row.serviced && (
                          <button
                            type="button"
                            onClick={() => setClientToAssign(c)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            title="Assign to technician"
                          >
                            <UserPlus className="h-4 w-4" />
                            Assign
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientDetailsModal
        client={selectedClient}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedClient(null);
        }}
      />

      <AssignClientModal
        client={clientToAssign}
        isOpen={!!clientToAssign}
        onClose={handleAssignClose}
      />
    </div>
  );
}
